import jsPDF from 'jspdf';

export interface PDFRecommendationData {
  recommendation: string;
  userInfo: Record<string, string>;
  timestamp: string;
}

class PDFService {
  private addHeader(doc: jsPDF) {
    // Add Flashgate branding
    doc.setFontSize(24);
    doc.setTextColor(59, 130, 246); // Blue color
    doc.text('FlashgateAI', 20, 30);

    doc.setFontSize(14);
    doc.setTextColor(107, 114, 128); // Gray color
    doc.text('Cloud Storage Optimization Report', 20, 40);

    // Add line separator
    doc.setDrawColor(229, 231, 235);
    doc.line(20, 50, 190, 50);
  }

  private addUserInfo(doc: jsPDF, userInfo: Record<string, string>, startY: number): number {
    let currentY = startY;

    doc.setFontSize(16);
    doc.setTextColor(31, 41, 55); // Dark gray
    doc.text('Your Information', 20, currentY);
    currentY += 15;

    doc.setFontSize(11);
    doc.setTextColor(75, 85, 99); // Medium gray

    Object.entries(userInfo).forEach(([key, value]) => {
      if (value && value.trim()) {
        const label = this.formatLabel(key);
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, 20, currentY);
        doc.setFont('helvetica', 'normal');

        // Handle long text wrapping
        const wrappedText = doc.splitTextToSize(value, 120);
        doc.text(wrappedText, 80, currentY);
        currentY += wrappedText.length * 5 + 3;
      }
    });

    return currentY + 10;
  }

  private addRecommendation(doc: jsPDF, recommendation: string, startY: number): number {
    let currentY = startY;

    doc.setFontSize(16);
    doc.setTextColor(31, 41, 55);
    doc.text('Optimization Recommendations', 20, currentY);
    currentY += 15;

    doc.setFontSize(11);
    doc.setTextColor(55, 65, 81);
    doc.setFont('helvetica', 'normal');

    // Split recommendation into paragraphs and handle wrapping
    const paragraphs = recommendation.split('\n\n');

    paragraphs.forEach((paragraph) => {
      if (paragraph.trim()) {
        const wrappedParagraph = doc.splitTextToSize(paragraph.trim(), 170);
        doc.text(wrappedParagraph, 20, currentY);
        currentY += wrappedParagraph.length * 5 + 8;

        // Add new page if needed
        if (currentY > 270) {
          doc.addPage();
          currentY = 20;
        }
      }
    });

    return currentY;
  }

  private addFooter(doc: jsPDF) {
    const pageCount = (doc as any).getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      // Add footer line
      doc.setDrawColor(229, 231, 235);
      doc.line(20, 280, 190, 280);

      // Add footer text
      doc.setFontSize(9);
      doc.setTextColor(107, 114, 128);
      doc.text('Generated by FlashgateAI - Cloud Storage Optimization Assistant', 20, 290);
      doc.text(`Page ${i} of ${pageCount}`, 170, 290);

      // Add timestamp
      const timestamp = new Date().toLocaleString();
      doc.text(`Generated on: ${timestamp}`, 20, 285);
    }
  }

  private formatLabel(key: string): string {
    const labelMap: Record<string, string> = {
      primaryObjective: 'Primary Objective',
      cloudProvider: 'Cloud Provider',
      monthlyBudget: 'Monthly Budget',
      storageProvider: 'Storage Provider',
      dataVolume: 'Data Volume',
      accessFrequency: 'Access Frequency',
      currentIssues: 'Current Challenges',
      complianceRequirements: 'Compliance Requirements',
    };

    return (
      labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())
    );
  }

  generateRecommendationPDF(data: PDFRecommendationData): void {
    const doc = new jsPDF();

    // Add header
    this.addHeader(doc);

    let currentY = 65;

    // Add user information if available
    if (Object.keys(data.userInfo).length > 0) {
      currentY = this.addUserInfo(doc, data.userInfo, currentY);
    }

    // Add recommendation
    this.addRecommendation(doc, data.recommendation, currentY);

    // Add footer
    this.addFooter(doc);

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `FlashgateAI_Optimization_Report_${timestamp}.pdf`;

    // Download the PDF
    doc.save(filename);
  }
}

export const pdfService = new PDFService();
